import { decomposeColor, recomposeColor, rgbToHex } from '@mui/material';
import { useEffect } from 'react';

/**
 * Keeps iOS Safari's status bar in step with what's on screen.
 *
 * Safari tints the strip behind the status bar from `<meta name="theme-color">`.
 * That strip sits directly above the app header, so when a modal dims the page
 * the header goes dark and the strip above it stays bright — a visible seam
 * across the top of the screen. Dimming theme-color to match closes it.
 *
 * Scope, learned the hard way on device:
 *  - This drives the STATUS BAR only. Safari's bottom toolbar has its own
 *    material and ignores theme-color — it stays light no matter what we write.
 *  - Only ever `setAttribute` on the tag index.html shipped. Removing that tag
 *    and appending a replacement stops Safari honouring theme-color at all, for
 *    the rest of the page's life, with no error. Don't "tidy up" duplicates.
 */

/** Applied when nothing is overlaying. Set by the theme provider. */
let baseColor = '';

/**
 * LIFO stack of open overlays.
 *
 * An entry with no `color` means "an overlay is up, use the header's colour" —
 * the normal case. Only a surface that covers the header outright (a fullscreen
 * dialog) names its own. Keeping presence separate from colour matters: the
 * `color-scheme` switch below keys off whether anything is open, so tying the
 * two together meant narrowing which modals set a colour silently stopped the
 * footer from going dark.
 */
const overlays: { color?: string }[] = [];

/**
 * Set just BEFORE a modal mounts, so the chrome recolours while the page is
 * still live. Once a modal is up the page can't scroll, and Safari stops
 * re-reading its chrome colours — so a change made at that point is ignored
 * until the modal closes. Recolouring first and mounting a beat later gives
 * Safari a window in which it will actually pick the new colour up.
 */
let primed = false;

/** The app header's undimmed colour. Published by the AppBar, which owns it. */
let headerBaseColor = '';

function apply() {
  if (typeof document === 'undefined') return;

  const overlaying = primed || overlays.length > 0;

  // The app header's colour, as a variable so it repaints without a React
  // render — through context it only updated when something else happened to
  // re-render the header, so it lagged a whole open/close cycle.
  //
  // This same value is what the status bar gets below, so the strip and the
  // header are the identical colour by construction. They used to be computed
  // separately — the header from its own palette entry, the status bar from
  // `primary` — which produced two slightly different greens.
  const headerColor = headerBaseColor
    ? overlaying
      ? flattenColor(BACKDROP_DIM, headerBaseColor)
      : headerBaseColor
    : '';

  if (headerColor) {
    document.documentElement.style.setProperty(
      '--app-header-color',
      headerColor,
    );
  }

  // Best-effort colour match on Safari's own toolbar, which is the one surface
  // we can't paint: while a modal is open the page can't scroll, Safari keeps
  // the toolbar expanded, and that band stops being page area at all
  // (elementFromPoint returns nothing there). No CSS reaches it.
  //
  // color-scheme is the one hook that changes how the browser renders its own
  // chrome. Switching to dark while an overlay is up asks Safari for its dark
  // toolbar material, which sits far closer to a dimmed page than the default
  // light one. Not a fix — the band is still Safari's, not ours — but the
  // mismatch goes from white-on-dark to dark-on-dark.
  document.documentElement.style.colorScheme = overlaying ? 'dark' : '';

  // Prefer an explicitly pushed colour (a fullscreen dialog naming its own
  // paper, since it covers the header entirely), then the header's colour, then
  // the theme's fallback for pages with no header at all.
  // Topmost overlay that named a colour wins; otherwise follow the header, so
  // the strip and the header are identical by construction.
  const named = [...overlays].reverse().find((o) => o.color)?.color;
  const color = named || headerColor || baseColor;
  if (!color) return;

  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', color);
}

/**
 * Flatten a translucent `overlay` over an opaque `under` into one opaque colour
 * — what the backdrop would composite to, without actually stacking anything.
 *
 * Hex out: every value known to be honoured by theme-color here is hex, and a
 * computed `rgb()` once left the bar untinted entirely.
 */
export function flattenColor(overlay: string, under: string): string {
  try {
    const top = decomposeColor(overlay);
    const bottom = decomposeColor(under);
    const alpha = top.values[3] ?? 1;

    const channel = (i: number) =>
      Math.round(top.values[i] * alpha + bottom.values[i] * (1 - alpha));

    return rgbToHex(
      recomposeColor({
        type: 'rgb',
        values: [channel(0), channel(1), channel(2)],
      }),
    );
  } catch {
    return under;
  }
}

/** The dim MUI's Backdrop paints. Kept here so the fake and the real match. */
export const BACKDROP_DIM = 'rgba(0, 0, 0, 0.3)';

/** The colour shown when nothing is overlaying. Called by the theme provider. */
export function setThemeColorBase(color: string) {
  baseColor = color;
  apply();
}

/** The app header's own undimmed colour. Published by the AppBar. */
export function setHeaderBaseColor(color: string) {
  headerBaseColor = color;
  apply();
}

/**
 * How long to hold between recolouring the chrome and mounting the modal. Long
 * enough for Safari to notice while the page is still scrollable, short enough
 * not to read as lag on the tap.
 */
export const OVERLAY_CHROME_LEAD_MS = 120;

/**
 * Recolour the chrome for an overlay that is about to open (`true`), or release
 * it (`false`). Call with `true`, wait {@link OVERLAY_CHROME_LEAD_MS}, then
 * mount — see `primed` above for why the order matters.
 */
export function setOverlayChromePrimed(value: boolean) {
  primed = value;
  apply();
}

/**
 * Register an open overlay, releasing it on close or unmount.
 *
 * @param active whether this surface is currently showing.
 * @param color  only for a surface that covers the app header outright (a
 *               fullscreen dialog), which therefore owns the status bar colour.
 *               Omit otherwise and the strip follows the header.
 */
export function useThemeColorOverlay(active: boolean, color?: string) {
  useEffect(() => {
    if (!active) return;

    const entry = { color };
    overlays.push(entry);
    apply();

    return () => {
      const idx = overlays.indexOf(entry);
      if (idx !== -1) overlays.splice(idx, 1);
      apply();
    };
  }, [active, color]);
}
